import { isEmpty } from "lodash";
import { DIFF_TYPES } from "../../context/utils/getCalculatedState";

/**
 * Compares two arrays of objects by a key field
 * Returns items that were added, removed, or changed
 */
const compareArrays = (before = [], after = [], keyField = "id") => {
  const beforeMap = new Map(before.map((item) => [item[keyField], item]));
  const afterMap = new Map(after.map((item) => [item[keyField], item]));

  const added = [];
  const removed = [];
  const changed = [];

  // Find added and changed items
  afterMap.forEach((afterItem, id) => {
    const beforeItem = beforeMap.get(id);
    if (!beforeItem) {
      added.push(afterItem);
    } else if (JSON.stringify(beforeItem) !== JSON.stringify(afterItem)) {
      changed.push({ before: beforeItem, after: afterItem });
    }
  });

  // Find removed items
  beforeMap.forEach((beforeItem, id) => {
    if (!afterMap.has(id)) {
      removed.push(beforeItem);
    }
  });

  return { added, removed, changed };
};

/**
 * Processes diff data into structured changelog entries
 * @param {Object} baseline - Baseline data (fromDate)
 * @param {Object} next - Next data (toDate)
 * @param {Object} teams - Teams with diff data from getDiffs
 * @param {Object} members - Members with diff data from getDiffs
 * @param {Object} dateRange - Date range object with from and to dates
 * @returns {Object} Processed changelog data with entries grouped by type
 */
export const processChangelogData = (
  baseline,
  next,
  teams,
  members,
  dateRange = {}
) => {
  const entries = {
    teams: [],
    members: [],
    allocations: [],
    attributes: [],
    tags: [],
  };

  // Process team changes (already calculated by getDiffs)
  Object.values(teams).forEach((team) => {
    if (!team.diff || isEmpty(team.diff)) {
      return;
    }

    const baselineTeam = baseline?.teams?.[team.id];
    const nextTeam = next?.teams?.[team.id];

    const teamName = team.name || team.id;
    Object.entries(team.diff).forEach(([teamId, diffEntry]) => {
      const diffType = diffEntry._type;

      if (diffType === DIFF_TYPES.ADDED) {
        entries.teams.push({
          type: "added",
          entityId: team.id,
          entityName: teamName,
          entity: team, // Full team object for badge rendering
          from: null,
          to: diffEntry.to || teamId,
          toTeam: teams[diffEntry.to || teamId], // Full team object for badge
          description: `Team "${teamName}" was added`,
          changeDate: dateRange.to || null,
          fromDate: dateRange.from || null,
        });
      } else if (diffType === DIFF_TYPES.REMOVED) {
        entries.teams.push({
          type: "removed",
          entityId: team.id,
          entityName: teamName,
          entity: team, // Full team object for badge rendering
          from: diffEntry.from || teamId,
          fromTeam: teams[diffEntry.from || teamId], // Full team object for badge
          to: null,
          description: `Team "${teamName}" was removed`,
          changeDate: dateRange.to || null,
          fromDate: dateRange.from || null,
        });
      } else if (
        diffType === DIFF_TYPES.MOVED_IN ||
        diffType === DIFF_TYPES.MOVED_OUT
      ) {
        const fromTeamObj = teams[diffEntry.from];
        const toTeamObj = teams[diffEntry.to];
        const fromTeam = fromTeamObj?.name || diffEntry.from;
        const toTeam = toTeamObj?.name || diffEntry.to;
        entries.teams.push({
          type: "moved",
          entityId: team.id,
          entityName: teamName,
          entity: team, // Full team object for badge rendering
          from: diffEntry.from,
          to: diffEntry.to,
          fromName: fromTeam,
          toName: toTeam,
          fromTeam: fromTeamObj, // Full team object for badge
          toTeam: toTeamObj, // Full team object for badge
          description: `Team "${teamName}" moved from "${fromTeam}" to "${toTeam}"`,
          changeDate: dateRange.to || null,
          fromDate: dateRange.from || null,
        });
      }
    });

    // Check for attribute changes
    if (baselineTeam && nextTeam) {
      const attrDiff = compareArrays(
        baselineTeam.attributes || [],
        nextTeam.attributes || [],
        "id"
      );
      if (
        attrDiff.added.length > 0 ||
        attrDiff.removed.length > 0 ||
        attrDiff.changed.length > 0
      ) {
        entries.attributes.push({
          type: "changed",
          entityId: team.id,
          entityName: teamName,
          entityType: "team",
          added: attrDiff.added,
          removed: attrDiff.removed,
          changed: attrDiff.changed,
          description: `Team "${teamName}" attributes changed`,
          changeDate: dateRange.to || null,
          fromDate: dateRange.from || null,
        });
      }

      // Check for tag changes
      const tagDiff = compareArrays(
        baselineTeam.tags || [],
        nextTeam.tags || [],
        "id"
      );
      if (
        tagDiff.added.length > 0 ||
        tagDiff.removed.length > 0 ||
        tagDiff.changed.length > 0
      ) {
        entries.tags.push({
          type: "changed",
          entityId: team.id,
          entityName: teamName,
          entityType: "team",
          added: tagDiff.added,
          removed: tagDiff.removed,
          changed: tagDiff.changed,
          description: `Team "${teamName}" tags changed`,
          changeDate: dateRange.to || null,
          fromDate: dateRange.from || null,
        });
      }
    }
  });

  // Helper function to look up team from all available sources (current, baseline, next)
  const lookupTeam = (teamId) => {
    if (!teamId) {
      return null;
    }
    // First check current teams
    if (teams[teamId]) {
      return teams[teamId];
    }
    // Then check baseline teams
    if (baseline?.teams?.[teamId]) {
      return baseline.teams[teamId];
    }
    // Finally check next teams
    if (next?.teams?.[teamId]) {
      return next.teams[teamId];
    }
    return null;
  };

  // Process member changes (already calculated by getDiffs)
  // Use a Set to track unique entries and prevent duplicates
  const memberEntryKeys = new Set();
  // Track MOVED_OUT and MOVED_IN entries separately to combine them later
  const movedOutEntries = new Map(); // key: memberId, value: entry
  const movedInEntries = new Map(); // key: memberId, value: entry

  Object.values(members).forEach((member) => {
    if (!member.diff || isEmpty(member.diff)) {
      return;
    }

    const memberName =
      `${member.preferredName || member.firstName || ""} ${
        member.surname || ""
      }`.trim() || member.aggregateId;

    Object.entries(member.diff).forEach(([teamId, diffEntry]) => {
      const diffType = diffEntry._type;
      const teamName = teams[teamId]?.name || teamId;

      const teamObj = teams[teamId];

      // Create a unique key for this entry to prevent duplicates
      const entryKey = `${member.aggregateId}-${diffType}-${teamId || ""}`;

      // Skip if we've already processed this exact change
      if (memberEntryKeys.has(entryKey)) {
        return;
      }
      memberEntryKeys.add(entryKey);

      if (diffType === DIFF_TYPES.NEW) {
        entries.members.push({
          type: "new",
          entityId: member.aggregateId,
          entityName: memberName,
          entity: member, // Full member object for badge rendering
          from: null,
          to: teamId,
          toName: teamName,
          toTeam: teamObj, // Full team object for badge
          description: `Member "${memberName}" was added to team "${teamName}"`,
          changeDate: dateRange.to || null,
          fromDate: dateRange.from || null,
        });
      } else if (diffType === DIFF_TYPES.REMOVED) {
        entries.members.push({
          type: "removed",
          entityId: member.aggregateId,
          entityName: memberName,
          entity: member, // Full member object for badge rendering
          from: teamId,
          fromName: teamName,
          fromTeam: teamObj, // Full team object for badge
          to: null,
          description: `Member "${memberName}" was removed from team "${teamName}"`,
          changeDate: dateRange.to || null,
          fromDate: dateRange.from || null,
        });
      } else if (diffType === DIFF_TYPES.MOVED_OUT) {
        // Store MOVED_OUT entry for potential combination with MOVED_IN
        // Use diffEntry.from if available, otherwise use teamId (team they're leaving)
        const fromTeamId = diffEntry.from || teamId;
        const fromTeamObj = lookupTeam(fromTeamId);
        const fromTeamName = fromTeamObj?.name || fromTeamId;
        // Check if diffEntry has a 'to' team (destination team)
        const toTeamId = diffEntry.to || null;
        const toTeamObj = toTeamId ? lookupTeam(toTeamId) : null;
        const toTeamName = toTeamObj?.name || toTeamId;

        movedOutEntries.set(member.aggregateId, {
          type: "moved",
          entityId: member.aggregateId,
          entityName: memberName,
          entity: member,
          from: fromTeamId,
          fromName: fromTeamName,
          fromTeam: fromTeamObj,
          to: toTeamId,
          toName: toTeamName,
          toTeam: toTeamObj,
          description: toTeamId
            ? `Member "${memberName}" moved from "${fromTeamName}" to "${toTeamName}"`
            : `Member "${memberName}" moved from team "${fromTeamName}"`,
          changeDate: dateRange.to || null,
          fromDate: dateRange.from || null,
        });
      } else if (diffType === DIFF_TYPES.MOVED_IN) {
        // Store MOVED_IN entry for potential combination with MOVED_OUT
        // Use diffEntry.to if available, otherwise use teamId (team they're joining)
        const toTeamId = diffEntry.to || teamId;
        const toTeamObj = lookupTeam(toTeamId);
        const toTeamName = toTeamObj?.name || toTeamId;
        // Check if diffEntry has a 'from' team (source team)
        const fromTeamId = diffEntry.from || null;
        const fromTeamObj = fromTeamId ? lookupTeam(fromTeamId) : null;
        const fromTeamName = fromTeamObj?.name || fromTeamId;

        movedInEntries.set(member.aggregateId, {
          type: "moved",
          entityId: member.aggregateId,
          entityName: memberName,
          entity: member,
          from: fromTeamId,
          fromName: fromTeamName,
          fromTeam: fromTeamObj,
          to: toTeamId,
          toName: toTeamName,
          toTeam: toTeamObj,
          description: fromTeamId
            ? `Member "${memberName}" moved from "${fromTeamName}" to "${toTeamName}"`
            : `Member "${memberName}" moved to team "${toTeamName}"`,
          changeDate: dateRange.to || null,
          fromDate: dateRange.from || null,
        });
      }
    });
  });

  // Combine MOVED_OUT and MOVED_IN entries into single "moved" entries
  movedOutEntries.forEach((movedOutEntry, memberId) => {
    const movedInEntry = movedInEntries.get(memberId);
    if (movedInEntry) {
      // Combine into single entry with both from and to teams
      // Prefer the most complete data: use fromTeam from MOVED_OUT, toTeam from MOVED_IN
      // But if either entry already has both teams (from diffEntry.to/from), use those
      const fromTeamId = movedOutEntry.from || movedInEntry.from;
      const fromTeamObj = movedOutEntry.fromTeam || movedInEntry.fromTeam;
      const fromTeamName =
        movedOutEntry.fromName || movedInEntry.fromName || fromTeamId;

      const toTeamId = movedInEntry.to || movedOutEntry.to;
      const toTeamObj = movedInEntry.toTeam || movedOutEntry.toTeam;
      const toTeamName =
        movedInEntry.toName || movedOutEntry.toName || toTeamId;

      entries.members.push({
        type: "moved",
        entityId: movedOutEntry.entityId,
        entityName: movedOutEntry.entityName,
        entity: movedOutEntry.entity,
        from: fromTeamId,
        fromName: fromTeamName,
        fromTeam: fromTeamObj,
        to: toTeamId,
        toName: toTeamName,
        toTeam: toTeamObj,
        description: `Member "${movedOutEntry.entityName}" moved from "${fromTeamName}" to "${toTeamName}"`,
        changeDate: movedOutEntry.changeDate || movedInEntry.changeDate || null,
        fromDate: movedOutEntry.fromDate || movedInEntry.fromDate || null,
      });
      // Remove from movedInEntries so we don't process it again
      movedInEntries.delete(memberId);
    } else {
      // Only MOVED_OUT, no MOVED_IN - add as is (member left but didn't join another team)
      entries.members.push(movedOutEntry);
    }
  });

  // Add any remaining MOVED_IN entries that don't have a matching MOVED_OUT
  movedInEntries.forEach((movedInEntry) => {
    entries.members.push(movedInEntry);
  });

  // Process allocation and tag changes for members
  Object.values(members).forEach((member) => {
    const baselineMember = baseline?.members?.[member.aggregateId];
    const nextMember = next?.members?.[member.aggregateId];

    if (!baselineMember || !nextMember) {
      return;
    }

    const memberName =
      `${member.preferredName || member.firstName || ""} ${
        member.surname || ""
      }`.trim() || member.aggregateId;

    // Check for allocation changes
    if (baselineMember && nextMember) {
      const allocDiff = compareArrays(
        baselineMember.allocations || [],
        nextMember.allocations || [],
        "targetGroupId"
      );
      if (
        allocDiff.added.length > 0 ||
        allocDiff.removed.length > 0 ||
        allocDiff.changed.length > 0
      ) {
        entries.allocations.push({
          type: "changed",
          entityId: member.aggregateId,
          entityName: memberName,
          added: allocDiff.added,
          removed: allocDiff.removed,
          changed: allocDiff.changed,
          description: `Member "${memberName}" allocations changed`,
          changeDate: dateRange.to || null,
          fromDate: dateRange.from || null,
        });
      }

      // Check for FTE changes (if FTE changed but allocations didn't)
      const baselineFte = baselineMember.fte;
      const nextFte = nextMember.fte;
      if (baselineFte !== nextFte && allocDiff.changed.length === 0) {
        entries.allocations.push({
          type: "changed",
          entityId: member.aggregateId,
          entityName: memberName,
          fteChanged: true,
          fromFte: baselineFte,
          toFte: nextFte,
          description: `Member "${memberName}" FTE changed from ${baselineFte} to ${nextFte}`,
          changeDate: dateRange.to || null,
          fromDate: dateRange.from || null,
        });
      }

      // Check for tag changes on members
      const tagDiff = compareArrays(
        baselineMember.tags || [],
        nextMember.tags || [],
        "id"
      );
      if (
        tagDiff.added.length > 0 ||
        tagDiff.removed.length > 0 ||
        tagDiff.changed.length > 0
      ) {
        entries.tags.push({
          type: "changed",
          entityId: member.aggregateId,
          entityName: memberName,
          entityType: "member",
          added: tagDiff.added,
          removed: tagDiff.removed,
          changed: tagDiff.changed,
          description: `Member "${memberName}" tags changed`,
          changeDate: dateRange.to || null,
          fromDate: dateRange.from || null,
        });
      }
    }
  });

  return entries;
};
